import React, { ChangeEvent, useEffect, useState } from "react";

import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";

import { useGuestPortalContext } from "./GuestPortalContext";
import { useAppDispatch } from "hooks/redux";

import Page from "components/page/Page";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import { useTranslation } from "react-i18next";
import "./portalOverview.scss";
import CustomerImage from "elements/customer/CustomerImage";
import { DeleteCustomerProfileImage, PutCustomer, UploadCustomerProfileImage } from "api/rpc/2024-04/customer/customer";
import Popup from "components/popup/Popup";
import { UserLogout } from "api/rpc";
import { useHistory } from "react-router";
import { GetCustomerMemberships, ICustomerMembership } from "api/rpc/2024-04/customer/membership";
import { CancelToken } from "axios";
import { UserType } from "redux/reducers/models/user";
import { userLogout } from "redux/actions/auth";

function formatBirthDate(value: Date): string {
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");

  return `${value.getFullYear()}-${month}-${day}`;
}

function parseBirthDate(value: string): Date {
  return new Date(value.length === 10 ? `${value}T00:00:00` : value);
}

export default function PortalOverview() {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { portalState, updatePortalState } = useGuestPortalContext();
  const guest = portalState.guest;

  const [editState, setEditState] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    birth_date: null as string | null,
  });

  const [filterState, setFilterState] = useState({
    adminUserLoggedIn: false,
  });


  const [membershipState, setMembershipState] = useState<ICustomerMembership[]>(null);

  const { t } = useTranslation();
  useEffect(() => {
    if (portalState.guest) {
      setEditState({
        first_name: guest.first_name,
        last_name: guest.last_name,
        email: guest.email ?? "",
        phone: guest.phone ?? "",
        birth_date: guest.birth_date ?? null,
      });
    } void getMemberships();
  }, []);

  useEffect(() => {
    if (guest?.user_type === UserType.CLIENT_ADMIN || guest?.user_type === UserType.FACILITY_ADMIN) {
      setFilterState(prevState => ({ ...prevState, adminUserLoggedIn: true }));

      return;
    }
  }, [guest]);

  async function updateGuestProfile() {
    const params = {
      id: portalState.guest.id,
      ...editState,
    };
    const customerUpdateRes = await PutCustomer(params, true);

    if (customerUpdateRes.status === StatusCode.OK) {
      updatePortalState({ guest: customerUpdateRes.data });
      dispatch(showSuccess(t("Changes saved successfully.")));
      return;
    } else {
      dispatch(showError(customerUpdateRes?.message ?? t("Error saving changes")));
    }
  }

  async function getMemberships(cancelToken?: CancelToken) {
    if (membershipState !== null) {
      setMembershipState(null);
    }

    const membershipRes = await GetCustomerMemberships({ customer_id: portalState.guest.id, status: "active" }, false, cancelToken);

    if (membershipRes.status !== StatusCode.OK) {
      if (membershipRes?.message !== "Cancelled") {
        dispatch(showError(t("Error getting memeberships")));
      }
      return;
    }

    setMembershipState(membershipRes.data);
  }

  function handleOnChangeEvent(event: ChangeEvent<HTMLInputElement>) {
    const { id, value } = event.target;
    setEditState(prev => ({ ...prev, [id]: value }));
  }

  async function saveCustomerImage(imageFile: File) {
    const formData = new FormData();

    formData.append("profile_image", imageFile);

    const imageRes = await UploadCustomerProfileImage(formData, true);
    if (imageRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error uploading image")));
      return;
    }

    dispatch(showSuccess(t("Image was uploaded successfully")));

    const tempCustomer = { ...guest, ...imageRes.data };
    updatePortalState({ guest: tempCustomer });
  }

  async function handleRemovepicture() {
    const imageResponse = await DeleteCustomerProfileImage(true);
    if (imageResponse.status !== StatusCode.OK) {
      dispatch(showError(t("Error removing image")));
      return;
    }
    updatePortalState({ guest: imageResponse.data });
    dispatch(showSuccess(t("Image removed successfully")));
  }

  async function logoutUser() {
    const logoutRes = await UserLogout(true);

    if (logoutRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error logging out")));
      return;
    }

    dispatch(userLogout());
    updatePortalState({ guest: undefined });
    history.replace(`/tee-on/portal/login`);
  }

  return (
    <Page
      full
      secondaryActions={[
        {
          content: t("Save"),
          action: () => updateGuestProfile(),
        },
      ]}
      title={t("Account Overview")}
    >
      <Card title={t("Contact Details")}>
        <Card.Section>
          <FormLayout.Group>
            <CustomerImage
              circleImage
              imageSource={guest.profile_image_source}
              saveProfileImage={imageFile => saveCustomerImage(imageFile)}
              {...(guest.profile_image_source ? undefined : { deleteProfileImage: () => handleRemovepicture() })}
            />
          </FormLayout.Group>
          <br></br>
          <FormLayout.Group>
            <div id="first-name">
              <Input
                id="first_name"
                label={t("First Name")}
                className="basic-information-input"
                value={editState.first_name}
                onChange={(event: ChangeEvent<HTMLInputElement>) => handleOnChangeEvent(event)}
                disabled={membershipState?.length > 0}
                locked={membershipState?.length > 0}
              />
            </div>
            <div id="last-name">
              <Input
                id="last_name"
                label={t("Last Name")}
                className="basic-information-input"
                value={editState.last_name}
                onChange={(event: ChangeEvent<HTMLInputElement>) => handleOnChangeEvent(event)}
                disabled={membershipState?.length > 0}
                locked={membershipState?.length > 0}
              />
            </div>
          </FormLayout.Group>
          <FormLayout.Group>
            <div>
              <Input
                id="email"
                label={t("Email")}
                className="basic-information-input"
                value={editState.email}
                onChange={(event: ChangeEvent<HTMLInputElement>) => handleOnChangeEvent(event)}
              />
            </div>
            <div>
              <Input
                id="phone"
                label={t("Phone Number")}
                type="text"
                className="basic-information-input"
                value={editState.phone}
                onChange={(event: ChangeEvent<HTMLInputElement>) => handleOnChangeEvent(event)}
              />
            </div>
          </FormLayout.Group>
          <FormLayout.Group>
            <div>
              <DatePickerInput
                months={1}
                position="left"
                label={t("Birthday")}
                startingDate={editState.birth_date ? parseBirthDate(editState.birth_date) : undefined}
                setStartingDate={(selectedDate: Date) =>
                  setEditState(prev => ({ ...prev, birth_date: formatBirthDate(selectedDate) }))
                }
                allowEmptySingleDate
              />
            </div>
            <div></div>
          </FormLayout.Group>
        </Card.Section>
      </Card>

      <Popup
        open={filterState.adminUserLoggedIn}
        type="warning"
        title={t("Admin Account Detected")}
        description={t("Looks like you are logged into an admin account. Click logout below to view account overview")}
        onOk={logoutUser}
        okText={t("Logout")}
      />
    </Page>
  );
}
